import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { useAcceptInvite, useInvitePreview } from '../api/hooks'
import { GameThumb, Pill, ScorePad, ScorePadRow, Wordmark } from '../primitives'

export const InviteLandingScreen = () => {
  const { token } = useParams({ from: '/invite/$token' })
  const navigate = useNavigate()
  const preview = useInvitePreview(token)
  const accept = useAcceptInvite()
  const [acceptError, setAcceptError] = useState<string | null>(null)

  const onJoin = async () => {
    setAcceptError(null)
    try {
      const res = await accept.mutateAsync(token)
      navigate({ to: '/group/$id', params: { id: res.groupId } })
    } catch (err) {
      setAcceptError(err instanceof ApiError ? err.friendlyMessage : 'Could not join')
    }
  }

  return (
    <div className="paper" style={{ minHeight: '100vh' }}>
      <header
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--line-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Wordmark size={22} />
        <div className="kicker" style={{ fontSize: 10 }}>
          group invite
        </div>
      </header>

      <main style={{ padding: '24px 16px' }}>
        {preview.isLoading && <div style={{ color: 'var(--ink-3)' }}>Loading…</div>}

        {preview.error && (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              color: 'var(--ink-2)',
            }}
          >
            <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 22 }}>
              {preview.error instanceof ApiError && preview.error.status === 410
                ? 'This invite is no longer valid.'
                : 'Invite not found.'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              Ask the group admin for a fresh link.
            </div>
            <Link
              to="/home"
              style={{
                marginTop: 8,
                fontSize: 13,
                color: 'var(--terra-ink)',
                textDecoration: 'none',
              }}
            >
              ← Back to home
            </Link>
          </div>
        )}

        {preview.data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <div className="kicker" style={{ fontSize: 11 }}>
                you're invited to
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 26,
                  marginTop: 2,
                }}
              >
                {preview.data.groupName}
              </div>
              {preview.data.groupDescription && (
                <div
                  style={{
                    marginTop: 6,
                    fontFamily: 'var(--font-serif)',
                    fontStyle: 'italic',
                    fontSize: 16,
                    color: 'var(--ink-2)',
                  }}
                >
                  "{preview.data.groupDescription}"
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <Pill color="felt">
                  {preview.data.memberCount} member{preview.data.memberCount === 1 ? '' : 's'}
                </Pill>
              </div>
            </div>

            {preview.data.sampleGames.length > 0 && (
              <div>
                <div className="kicker" style={{ marginBottom: 6 }}>
                  what they play
                </div>
                <ScorePad>
                  {preview.data.sampleGames.map((g) => (
                    <ScorePadRow key={g.id}>
                      <GameThumb label={g.name} size={32} />
                      <div style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{g.name}</div>
                    </ScorePadRow>
                  ))}
                </ScorePad>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {acceptError && (
                <div
                  style={{
                    padding: 10,
                    background: 'var(--terra-bg)',
                    color: 'var(--terra-ink)',
                    border: '1px solid var(--terra)',
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                >
                  {acceptError}
                </div>
              )}
              <button
                type="button"
                className="btn-stamp"
                onClick={onJoin}
                disabled={accept.isPending}
                style={{ padding: '12px 14px' }}
              >
                {accept.isPending ? 'Joining…' : 'Join group'}
              </button>
              <Link
                to="/home"
                style={{
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--ink-3)',
                  textDecoration: 'none',
                }}
              >
                Not now
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
